import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import { InfoTip } from "./ReceiverMFCASummary";
import { fmtLbs, fmtPct, reasonLabel } from "../../utils/inventory/receiverMfcaUtils";
import "../../style/mfca-dashboard.css";

const rateColor = (pct) => {
  if (pct >= 95) return "#669d2e";
  if (pct >= 85) return "#e0a030";
  return "#d4574e";
};

const pctClass = (pct) => {
  if (pct >= 95) return "is-good";
  if (pct >= 85) return "is-warn";
  return "is-bad";
};

const ReceiverProblemCarousel = ({
  title,
  iconClass,
  icon,
  items,
  emptyCaption,
  infoText,
}) => {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setIdx(0);
  }, [items.length]);

  const isEmpty = items.length === 0;
  const current = items[idx];
  // arrows are inert with a single item — keep the footer rendered for layout
  // and visual consistency with the prototype, but make the controls obviously
  // unclickable so users don't poke at them expecting nav.
  const navDisabled = items.length <= 1;

  const prev = () => setIdx((i) => (i - 1 + items.length) % items.length);
  const next = () => setIdx((i) => (i + 1) % items.length);

  return (
    <div className="mfca-card rl-attention-card">
      <div className="mfca-card-header">
        <div className={`mfca-card-icon ${iconClass}`}>
          <FontAwesomeIcon icon={icon} />
        </div>
        <h5 className="mfca-card-title">
          {title}
          <InfoTip text={infoText} />
        </h5>
      </div>

      {isEmpty ? (
        <div className="rl-empty-state">
          <span className="rl-empty-state-line">
            <FontAwesomeIcon icon={faCircleCheck} />
            {title === t("Materials Needing Attention")
              ? t("No materials needing attention")
              : t("No donors needing attention")}
          </span>
          <span className="rl-empty-state-caption">{emptyCaption}</span>
        </div>
      ) : (
        <>
          <div className="rl-problem-list rl-problem-list--carousel">
            <div className="rl-problem-row">
              <div className="rl-problem-row-top">
                <span className="rl-problem-row-name-group">
                  <span className="rl-problem-row-rank">#{idx + 1}</span>
                  <span className="rl-problem-row-name">{current.name}</span>
                </span>
                <span className={`rl-problem-row-pct ${pctClass(current.pct)}`}>
                  {fmtPct(current.pct)} {t("acceptance")}
                </span>
              </div>
              <div className="rl-problem-row-bar">
                <div
                  className="rl-problem-row-bar-fill"
                  style={{
                    width: `${Math.max(0, Math.min(100, current.pct))}%`,
                    background: rateColor(current.pct),
                  }}
                />
              </div>
              <div className="rl-problem-row-meta">
                <span>
                  {fmtLbs(current.rejectedLbs)} {t("lbs rejected of")}{" "}
                  {fmtLbs(current.deliveredLbs)} {t("lbs delivered")}
                </span>
                {current.topReason && (
                  <span>
                    <em>{t("top reason")}: {t(reasonLabel(current.topReason))}</em>
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="rl-carousel-footer">
            <button
              type="button"
              className={`rl-carousel-arrow ${navDisabled ? "is-disabled" : ""}`}
              onClick={navDisabled ? undefined : prev}
              aria-disabled={navDisabled}
              aria-label={t("Previous")}
              tabIndex={navDisabled ? -1 : 0}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="rl-carousel-position">
              {idx + 1} {t("of")} {items.length}
            </span>
            <button
              type="button"
              className={`rl-carousel-arrow ${navDisabled ? "is-disabled" : ""}`}
              onClick={navDisabled ? undefined : next}
              aria-disabled={navDisabled}
              aria-label={t("Next")}
              tabIndex={navDisabled ? -1 : 0}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

ReceiverProblemCarousel.propTypes = {
  title: PropTypes.string.isRequired,
  iconClass: PropTypes.string.isRequired,
  icon: PropTypes.object.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      pct: PropTypes.number.isRequired,
      deliveredLbs: PropTypes.number,
      rejectedLbs: PropTypes.number,
      topReason: PropTypes.string,
    })
  ).isRequired,
  emptyCaption: PropTypes.string.isRequired,
  infoText: PropTypes.string.isRequired,
};

export default ReceiverProblemCarousel;
